import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef } from "react";
import { useProgressNavigation } from "@/components/progress-bar";
import { useUser } from "@/contexts/user-provider";
import { parseError } from "@/lib/parse-error";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { toast } from "@/lib/use-toast";
import { QuestionType } from "@/types/forms/question";
import type { NoteCreate } from "@/types/notes";
import { duplicateSuperbill, getSuperbillByNote } from "@/api/provider/billing/superbill";
import { useCreateNoteMutation } from "@/api/provider/scribe/note-mutation";
import { duplicateNoteAttachment, getNoteAttachments, getNoteDetail } from "@/api/provider/scribe/notes";
export function useDuplicateNote() {
  const {
    currentUser
  } = useUser();
  const queryClient = useQueryClient();
  // The copy reads the source's link and writes a new bill. Skip the whole step
  // for users without billing access rather than firing calls that 403 — same
  // guard the note menu puts on its own Superbill actions.
  const canCopySuperbill = hasPermission(currentUser, PERMISSIONS.BILLING_READ) && hasPermission(currentUser, PERMISSIONS.BILLING_INVOICES_WRITE);
  const createNoteMutation = useCreateNoteMutation();
  const {
    navigateWithProgress,
    runWithNavigationGuard
  } = useProgressNavigation();
  const hasConfirmedDuplicateRef = useRef(false);
  const duplicateNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      if (!currentUser?.id) {
        throw new Error("User not authenticated");
      }
      const detail = await getNoteDetail(noteId);
      if (!detail) {
        throw new Error("Failed to load note details");
      }

      // Supplies are what this visit took from stock; copying them would take
      // the same stock again for a visit that hasn't happened.
      const supplyQuestionIds = new Set((detail.template?.questions ?? []).filter(q => q.questionType === QuestionType.SUPPLIES_USED).map(q_0 => q_0.id));
      const answers = detail.answers?.filter(a => !supplyQuestionIds.has(a.questionId)).map(a_0 => ({
        questionId: a_0.questionId,
        data: a_0.data
      }));

      // No eventId: the server links the copy to the patient's appointment on
      // the day it's made, not the source note's visit.
      const duplicatePayload: NoteCreate = {
        patientId: detail.patient.id,
        providerId: currentUser.id,
        parentNoteId: undefined,
        aiGenerated: detail.aiGenerated,
        title: detail.title,
        noteTranscriptId: detail.transcript?.id,
        template: detail.template,
        locationId: detail.locationId,
        answers,
        icd10Codes: detail.icd10Codes?.map(c => c.code),
        cptCodes: detail.cptCodes?.map(c_0 => c_0.code),
        cptModifiers: detail.cptModifiers
      };
      const newNote = await createNoteMutation.mutateAsync(duplicatePayload, {
        onError: () => {}
      });

      // Copy the source note's Super Bill onto the duplicate. It has to be a
      // copy: the new note is a new chain, and a chain links to at most one bill,
      // so pointing it at the source's bill would take that bill off the note it
      // was built for.
      //
      // Never fatal — the note itself is already created by this point, so a
      // billing failure must not surface as "the duplicate failed" and invite a
      // retry that mints a second note. Say what didn't happen instead.
      if (newNote && canCopySuperbill) {
        try {
          const linked = await getSuperbillByNote(detail.patient.id, noteId);
          if (linked) {
            const copy = await duplicateSuperbill(linked.id, newNote.id);
            if (!copy) throw new Error("The server returned an error.");
            // Seed the new note's link instead of letting its Superbill tab
            // discover it: that read can lag the just-committed write and
            // return null, which shows the "Create Superbill" CTA and invites a
            // second bill that steals the link from this copy.
            queryClient.setQueryData(["superbill-by-note", detail.patient.id, newNote.id], copy);
          }
        } catch {
          toast({
            title: "Superbill not copied",
            description: "The note was duplicated, but its Superbill wasn't. Attach one from the new note's menu.",
            variant: "error"
          });
        }
      }

      // Duplicate attachments from the original note, linking them to the new note
      if (newNote) {
        const attachments = await getNoteAttachments(noteId);
        if (attachments?.length) {
          await Promise.all(attachments.map(a_1 => duplicateNoteAttachment(a_1.id, newNote.id)));
        }
      }
      return newNote;
    },
    onSuccess: newNote_0 => {
      if (newNote_0) {
        navigateWithProgress(`/scribe/playground/${newNote_0.id}`, true, {
          bypassUnsavedChangesGuard: hasConfirmedDuplicateRef.current
        });
      }
    },
    onError: async error => {
      const description = await parseError(error);
      toast({
        title: "Error Duplicating Note",
        description,
        variant: "error"
      });
    },
    onSettled: () => {
      hasConfirmedDuplicateRef.current = false;
    }
  });
  const mutate: typeof duplicateNoteMutation.mutate = useCallback((noteId_0, options) => {
    runWithNavigationGuard(`/scribe/playground/${noteId_0}/duplicate`, () => {
      hasConfirmedDuplicateRef.current = true;
      duplicateNoteMutation.mutate(noteId_0, options);
    });
  }, [duplicateNoteMutation, runWithNavigationGuard]);
  return useMemo(() => ({
    ...duplicateNoteMutation,
    mutate
  }), [duplicateNoteMutation, mutate]);
}